import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Inject, Input, OnInit } from '@angular/core';
import { IMODALS_SERVICE, IModalsService, P500UIBaseModal } from '@platform500services/p500-ui-kit';
import { HttpClient } from '@angular/common/http';
import { IEnvironmentService } from '../../services/environment/environment.interface';
import { FormControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IDataService, IParsedDataResponse } from '../../services/data/data-service.interface';
import { IMetricsService } from '../../../modules/settings/crm-metrics/services/metrics-service.interface';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { PageServiceInterface } from '../../services/page/page-service.interface';
import { BaseMetricSettings } from './interfaces/base-metric-settings.interface';
import { BaseMetricRequest } from './interfaces/base-metric-request.interface';
import { Router } from '@angular/router';
import { MODAL_TOOLTIP_KEY_ENUM } from '../../components/form-fields/controls-with-hint/hints';
import {
  aggregateLocalization,
  dateTypeLocalization,
  formatLocalization,
  modelsLocalization,
  signsLocalization,
  targetsLocalization,
} from '../../components/base-metric-card/helpers/base-metrics.helpers';
import { CONDITIONS_TRANSLATIONS } from '../../services/data/data-constants-translations';

@Component({
  selector: 'gtd-base-terms-modal',
  templateUrl: './base-terms.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@DestroySubscribers()
export class BaseTermsComponent extends P500UIBaseModal implements OnInit {
  @Input() id: number;

  @HostBinding('class') get className() {
    return `c-modal c-modal--d c-modal--action-form -primary ${this.isLoading ? 'c-modal--is--isLoading' : ''}`;
  }

  public models: { [key: string]: string };
  public targets: { [key: string]: string };
  public filters: { [key: string]: string };
  public aggregates: { [key: string]: string };
  public formats: { [key: string]: string };
  public dateTypes: { [key: string]: string };

  private api: string;
  public isLoading = true;
  public selectData: BaseMetricSettings;
  public globalData: IParsedDataResponse;
  public title: string;
  public description: string;
  public form = new UntypedFormGroup({
    name: new UntypedFormControl(),
    metric_type: new UntypedFormControl(),
    format: new UntypedFormControl(),
    model: new UntypedFormControl(),
    filters: new UntypedFormArray([]),
    filter_sign: new UntypedFormControl(),
    filter_value: new UntypedFormControl(),
    target: new UntypedFormControl(),
    aggregate: new UntypedFormControl(),
    date_type: new UntypedFormControl(),
    description: new UntypedFormControl(),
    post_processor: new UntypedFormControl(),
    post_processor_on: new FormControl(false),
  });
  public MODAL_TOOLTIP_KEY_ENUM = MODAL_TOOLTIP_KEY_ENUM;

  subscribers: any = {};

  constructor(
    @Inject(IMODALS_SERVICE) public modalsService: IModalsService,
    @Inject('IEnvironmentService') private envService: IEnvironmentService,
    @Inject('IDataService') private dataService: IDataService,
    @Inject('IMetricsService') private metricsService: IMetricsService,
    @Inject('PageServiceInterface') private pageService: PageServiceInterface,
    private http: HttpClient,
    public cd: ChangeDetectorRef,
    public router: Router
  ) {
    super(modalsService, router);
    this.api = (this.envService.getVar('services.api') as string).split('/api')[0];
    this.globalData = this.dataService.getData();
  }

  ngOnInit(): void {
    this.subscribers.metricsSettings = this.metricsService.getMetricsSettings().subscribe((data: any) => {
      this.selectData = data;
      data.models.forEach((value) => {
        value.name = modelsLocalization.get(value.name) ?? value.name;
      });
      this.models = this.reduceOptions(data.models, 'model', 'name');
      data.formats[0].forEach((value) => {
        value.label = formatLocalization.get(value.label) ?? value.label;
      });
      this.formats = this.reduceOptions(data.formats[0], 'namespace', 'label');
      this.isLoading = false;

      if (this.id) {
        this.title = $localize`:@@settings.edit_base_metric:Edit Base Metric`;
        this.description = $localize`:@@settings.edit_base_metric_description:Edit of Base Metric`;
        this.http
          .post(`${this.api}/api/metrics/find`, {
            id: this.id,
          })
          .subscribe((metricData: any) => {
            const model = this.selectData.models.find((m) => m.model === metricData.model);
            const target = model.targets.find((t) => t.target === metricData.target);
            const aggregate = target?.aggregates.find((a) => a.aggregate === metricData.aggregate);

            this.nameControl.setValue(metricData.name);
            this.modelControl.setValue(metricData.model);
            this.formatControl.setValue(metricData.format);
            this.descriptionControl.setValue(metricData.description);
            this.dateTypeControl.setValue(metricData.date_type);
            if (target) {
              this.targetControl.setValue(metricData.target);
            }
            if (aggregate) {
              this.aggregateControl.setValue(metricData.aggregate);
            }
            if (metricData.filters) {
              const filters = JSON.parse(metricData.filters);
              filters.forEach((filter) => {
                const group = new UntypedFormGroup({
                  field: new UntypedFormControl(filter.field, Validators.required),
                  operator: new UntypedFormControl(filter.operator, Validators.required),
                  value: new UntypedFormControl(filter.values, Validators.required),
                });
                this.filtersControl.push(group);
              });
            }
            if (metricData.post_processor) {
              this.postProcessorOnControl.setValue(true);
            }
          });
      } else {
        this.title = $localize`:@@settings.create_base_metric:Create Base Metric`;
        this.description = $localize`:@@settings.create_base_metric_description:Creation of Base Metric`;
      }
      this.cd.markForCheck();
    });
  }

  public onModelChange(modelKey: string): void {
    const model = this.selectData.models.find((m) => m.model === modelKey);
    model.targets.forEach((value) => {
      value.label = targetsLocalization.get(value.label) ?? value.label;
    });
    this.targets = this.reduceOptions(model.targets, 'target', 'label');
    model.filters.forEach((value) => {
      value.label = CONDITIONS_TRANSLATIONS[value.label] ?? value.label;
    });
    this.filters = this.reduceOptions(model.filters, 'field', 'label');
    model.date_types.forEach((value) => {
      value.label = dateTypeLocalization.get(value.label) ?? value.label;
    });
    this.dateTypes = this.reduceOptions(model.date_types, 'namespace', 'label');

    this.dateTypeControl.setValue(null);
    this.targetControl.setValue(null);
    this.aggregateControl.setValue(null);
    this.filtersControl.clear();
  }

  public onTargetChange(targetKey: string): void {
    if (targetKey) {
      const model = this.selectData.models.find((m) => m.model === this.modelControl.value);
      const target = model.targets.find((t) => t.target === targetKey);
      this.aggregateControl.setValue(null);
      target.aggregates.forEach((aggregate) => {
        aggregate.label = aggregateLocalization.get(aggregate.label) ?? aggregate.label;
      });
      this.aggregates = this.reduceOptions(target.aggregates, 'aggregate', 'label');
      this.form.controls.post_processor.patchValue(target.post_processors[0]?.key);
    } else {
      this.postProcessorControl.patchValue(null);
    }
  }

  public removeFilter(i: number): void {
    this.filtersControl.removeAt(i);
  }

  submit(): void {
    this.isLoading = true;
    const filters = [];
    this.filtersControl.value.forEach((filter) => {
      filters.push({
        field: filter.field,
        operator: filter.operator,
        values: Array.isArray(filter.value) ? filter.value : [filter.value],
      });
    });

    const body: BaseMetricRequest = {
      metric_type: 'App\\Modules\\Metrics\\MetricTypes\\BaseMetric',
      formula: null,
      name: this.nameControl.value,
      model: this.modelControl.value,
      format: this.formatControl.value,
      aggregate: this.aggregateControl.value,
      target: this.targetControl.value,
      filters,
      date_type: this.dateTypeControl.value,
      description: this.descriptionControl.value,
      post_processor: this.postProcessorOnControl.value ? this.postProcessorControl.value : null,
    };

    if (this.id) {
      body.id = this.id;
    }

    this.http.post(`${this.api}/api/metrics/${this.id ? 'edit' : 'create'}`, body).subscribe(
      () => {
        this.pageService.refresh();
        this.close();
        this.metricsService.updateMetrics();
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  public addFilter(): void {
    const group = new UntypedFormGroup({
      field: new UntypedFormControl(null, Validators.required),
      operator: new UntypedFormControl(null, Validators.required),
      value: new UntypedFormControl(null, Validators.required),
    });
    this.filtersControl.push(group);
  }

  public getFiltersOptions(currentKey: string): { [key: string]: string } {
    const selectedFilters = this.filtersControl.controls.map((control) => {
      return (control as UntypedFormGroup).controls.field.value;
    });
    return Object.keys(this.filters).reduce(
      (acc, key) => {
        if (!selectedFilters.includes(key)) {
          acc[key] = this.filters[key];
        }
        return acc;
      },
      currentKey ? { [currentKey]: this.filters[currentKey] } : {}
    );
  }

  public getOperatorsOptions(filterKey: string): { [key: string]: string } {
    if (!filterKey) {
      return {};
    }

    const model = this.selectData.models.find((m) => m.model === this.modelControl.value);
    const filter = model.filters.find((f) => f.field === filterKey);
    filter.operators.forEach((value) => {
      value.label = signsLocalization.get(value.label) ?? value.label;
    });
    return this.reduceOptions(filter.operators, 'namespace', 'label');
  }

  private reduceOptions(array: any[], key: string, label: string): { [key: string]: string } {
    return array.reduce((acc, el) => {
      acc[el[key]] = el[label];
      return acc;
    }, {});
  }

  public getSelectType(filterKey: string): string {
    const type = this.getFilterType(filterKey);
    if (type === 'available_offer_currencies') {
      return type;
    }
    if (this.globalData[type] && Object.values(this.globalData[type]).every((value) => typeof value !== 'object')) {
      return 'global-data-select';
    }
    return type;
  }

  public getFilterType(filterKey: string): string {
    const model = this.selectData.models.find((m) => m.model === this.modelControl.value);
    const filter = model.filters.find((f) => f.field === filterKey);
    return filter?.type ?? '';
  }

  public conditionFieldChanged(group: UntypedFormGroup): void {
    group.get('operator').setValue(null);
    group.get('value').setValue(null);
  }

  get filtersControl(): UntypedFormArray {
    return this.form.get('filters') as UntypedFormArray;
  }

  get nameControl(): UntypedFormControl {
    return this.form.get('name') as UntypedFormControl;
  }

  get formatControl(): UntypedFormControl {
    return this.form.get('format') as UntypedFormControl;
  }

  get modelControl(): UntypedFormControl {
    return this.form.get('model') as UntypedFormControl;
  }

  get dateTypeControl(): UntypedFormControl {
    return this.form.get('date_type') as UntypedFormControl;
  }

  get targetControl(): UntypedFormControl {
    return this.form.get('target') as UntypedFormControl;
  }

  get aggregateControl(): UntypedFormControl {
    return this.form.get('aggregate') as UntypedFormControl;
  }

  get descriptionControl(): UntypedFormControl {
    return this.form.get('description') as UntypedFormControl;
  }

  get postProcessorOnControl(): FormControl {
    return this.form.get('post_processor_on') as FormControl;
  }

  get postProcessorControl(): FormControl<string> {
    return this.form.get('post_processor') as FormControl;
  }
}
